import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { Box, Avatar, Typography, CardContent, Divider } from "@mui/material";
import type { IComment } from "../types/comment";
import axiosService from "../utils/axios";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import type { SetImageBoxCommentData } from "./Comments";
import type { IPost } from "../types/post";
import type { IRecipeSetComment } from "./SavedRecipe";

// *** Component Specific Types *** //
interface ICommentProps {
    type: "ImageBox" | "Post" | "Recipe";
    comment: IComment;
    setData?: SetImageBoxCommentData | React.Dispatch<React.SetStateAction<IPost>> | IRecipeSetComment;
}

// ***** Main Component ***** //
const Comment: React.FC<ICommentProps> = ({ type, comment, setData = undefined }) => {
    // *** Root States *** //
    const authState = useSelector((state: RootState) => state.auth);
    const username = authState.account?.username;

    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    // *** Component States *** //
    const [commentLikes, setCommentLikes] = useState<string[]>(comment.likes);

    // *** Component Helpers *** //
    // Update the likes of the comments //
    const updateCommentLikes = async (commentId: string) => {
        if (!username) return;

        const res = await axiosService.post(`${process.env.REACT_APP_API_URL}/comments/updateLikes`, { commentId });
        const liked: boolean = res.data.body.liked;

        const next = liked
            ? commentLikes.includes(username)
                ? commentLikes
                : [...commentLikes, username]
            : commentLikes.filter((u) => u !== username);

        setCommentLikes(next);

        if (!setData) return;

        if (type === "ImageBox") {
            (setData as SetImageBoxCommentData)("commentLike", undefined, commentId);
        } else if (type === "Post") {
            (setData as React.Dispatch<React.SetStateAction<IPost>>)((localPostPrev) => ({
                ...localPostPrev,
                comments: localPostPrev.comments.map((c) => (c._id === commentId ? { ...c, likes: next } : c)),
            }));
        } else if (type === "Recipe") {
            (setData as (type: "newComment" | "newLike", newComments?: IComment[], commetId?: string) => void)(
                "newLike",
                undefined,
                commentId
            );
        }
    };

    return (
        <Box px={0} mb={2} key={comment._id}>
            <Box display="flex" alignItems="center" mb={2}>
                <Avatar src={comment.user.image ? comment.user.image.url : ""} alt={comment.user.username} />
                <Box ml={2}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {comment.user.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {new Date(comment.createdAt).toLocaleString()}
                    </Typography>
                </Box>
            </Box>
            <CardContent sx={{ pt: 0 }}>
                <Typography variant="body1">{comment.content}</Typography>
            </CardContent>
            <Box
                display="flex"
                onClick={() => updateCommentLikes(comment._id)}
                alignItems="center"
                gap={0.5}
                sx={{ cursor: "pointer", userSelect: "none", width: "40px" }}
            >
                {commentLikes.some((commentUser) => commentUser === username) ? (
                    <Favorite sx={{ color: theme.palette.primary.main }} fontSize="small" />
                ) : (
                    <FavoriteBorder fontSize="small" />
                )}
                <Typography variant="body2">{commentLikes.length}</Typography>
            </Box>

            <Divider sx={{ mt: 2 }} />
        </Box>
    );
};

export default Comment;
